import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  PoButtonModule,
  PoFieldModule,
  PoTableModule,
  PoTableColumn,
  PoTableAction,
  PoModalModule,
  PoModalComponent,
  PoModalAction,
  PoSelectOption
} from '@po-ui/ng-components';

import { Opcao, RecursoRegra, RegraBase } from '../shared/regras-api.service';
import { RegrasListaBase } from '../shared/regras-lista.base';
import { RegrasSkeletonComponent, RegrasToolbarComponent } from '../shared/regras-toolbar';
import { PoComboListaVisivelDirective } from '../../../shared/po-combo-lista-visivel.directive';

/** Regra de TES da NFS-e (tabela ZOJ). */
export interface RegraNfse extends RegraBase {
  codigo?: string;
  filial: string;
  tipoProcesso: string;
  geraFinanceiro: string;
  issRetido: string;
  outrasRet: string;
  cfop: string;
  tes: string;
  descricao: string;
  ordem: string;
  dtIni: string;
  dtFim: string;
  vigencia?: string;
}

/**
 * Aba de regras de classificação — NFS-e (ZOJ).
 *
 * Ao contrário do CT-e, a classificação da NFS-e não é tabela: é uma decisão
 * encadeada que depende de haver ou não retenção apurada no documento. Daí o
 * valor "Indiferente" em gera financeiro, ISS retido e demais retenções — ele
 * é o que permite as 11 regras caberem em 11 linhas em vez de 24 explodidas.
 *
 * A natureza financeira NÃO sai daqui: a NFS-e a resolve pelo perfil de
 * retenção, na aba de Natureza Financeira.
 */
@Component({
  selector: 'app-regras-nfse',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PoButtonModule,
    PoFieldModule,
    PoTableModule,
    PoModalModule,
    RegrasToolbarComponent,
    RegrasSkeletonComponent,
    PoComboListaVisivelDirective
  ],
  templateUrl: './nfse.html',
  styles: [`
    .regras-auditoria { color: var(--color-neutral-dark-70, #6e6e6e); }
    ::ng-deep .regras-form .po-row { margin-bottom: 1.5rem; }
    ::ng-deep .regras-form .po-row:last-child { margin-bottom: 0; }
  `]
})
export class RegrasNfseComponent extends RegrasListaBase<RegraNfse> {
  @ViewChild('formModal', { static: true }) formModal!: PoModalComponent;

  protected readonly recurso: RecursoRegra = 'nfse';
  protected readonly rotulo = 'as regras de NFS-e';

  private readonly fb = inject(FormBuilder);

  editing: RegraNfse | null = null;
  duplicando = false;

  readonly formGroup: FormGroup = this.fb.group({
    codigo: [''],
    filial: [''],
    tipoProcesso: ['', Validators.required],
    geraFinanceiro: ['', Validators.required],
    issRetido: ['', Validators.required],
    // Indiferente é o default: a maioria das regras vivas não olha as demais
    // retenções.
    outrasRet: ['3', Validators.required],
    cfop: [''],
    tes: ['', Validators.required],
    descricao: [''],
    ordem: ['', Validators.required],
    dtIni: [this.hojeIso()],
    dtFim: [''],
    ativo: ['1', Validators.required]
  });

  filiaisOptions: PoSelectOption[] = [];

  readonly tipoProcessoOptions: PoSelectOption[] = [
    { label: 'IPCOT (conta e ordem)', value: '1' },
    { label: 'Encomenda/Própria', value: '2' },
    { label: 'Sem processo', value: '3' }
  ];

  /** Sim / Não / Indiferente — o "indiferente" é o que evita explodir as regras. */
  readonly simNaoIndifOptions: PoSelectOption[] = [
    { label: 'Sim', value: '1' },
    { label: 'Não', value: '2' },
    { label: 'Indiferente', value: '3' }
  ];

  get filialSelecionada(): string {
    return this.formGroup.get('filial')?.value ?? '';
  }

  private _filtroTes: { filial: string } = { filial: '' };

  /** Referência estável: um literal no template refaz a busca a cada ciclo. */
  get filtroTes(): { filial: string } {
    const filial = this.filialSelecionada;
    if (this._filtroTes.filial !== filial) {
      this._filtroTes = { filial };
    }
    return this._filtroTes;
  }

  get modalTitle(): string {
    if (this.duplicando) { return 'Duplicar regra de NFS-e'; }
    return this.editing ? 'Editar regra de NFS-e' : 'Nova regra de NFS-e';
  }

  private readonly rotulosIndif = [
    { value: '1', label: 'Sim', color: 'color-10' },
    { value: '2', label: 'Não', color: 'color-07' },
    { value: '3', label: 'Indiferente', color: 'color-09' }
  ];

  readonly columns: Array<PoTableColumn> = [
    {
      property: 'codigo', label: 'Código', width: '110px', type: 'link',
      action: (_: string, row: RegraNfse) => this.editar(row)
    },
    { property: 'filial', label: 'Filial', width: '130px' },
    {
      property: 'tipoProcesso', label: 'Processo', width: '200px', type: 'label',
      labels: [
        { value: '1', label: 'IPCOT', color: 'color-08' },
        { value: '2', label: 'Encomenda/Própria', color: 'color-01' },
        { value: '3', label: 'Sem processo', color: 'color-11' }
      ]
    },
    { property: 'geraFinanceiro', label: 'Financeiro', width: '140px', type: 'label', labels: this.rotulosIndif },
    { property: 'issRetido', label: 'ISS retido', width: '140px', type: 'label', labels: this.rotulosIndif },
    { property: 'outrasRet', label: 'Demais ret.', width: '165px', type: 'label', labels: this.rotulosIndif },
    { property: 'cfop', label: 'CFOP', width: '100px' },
    { property: 'tes', label: 'TES', width: '100px' },
    { property: 'ordem', label: 'Ordem', width: '110px' },
    { property: 'vigencia', label: 'Vigência', width: '180px' },
    {
      property: 'ativo', label: 'Situação', width: '130px', type: 'label',
      labels: [
        { value: '1', label: 'Ativa', color: 'color-10' },
        { value: '2', label: 'Inativa', color: 'color-07' }
      ]
    },
    { property: 'descricao', label: 'Descrição' }
  ];

  readonly tableActions: Array<PoTableAction> = [
    { label: 'Editar', icon: 'an an-pencil-simple', action: (row: RegraNfse) => this.editar(row) },
    { label: 'Duplicar', icon: 'an an-copy', action: (row: RegraNfse) => this.duplicar(row) },
    {
      label: 'Inativar', icon: 'an an-lock', type: 'danger',
      action: (row: RegraNfse) => this.alterarSituacao(row, '2'),
      visible: (row: RegraNfse) => row.ativo === '1'
    },
    {
      label: 'Reativar', icon: 'an an-lock-open',
      action: (row: RegraNfse) => this.alterarSituacao(row, '1'),
      visible: (row: RegraNfse) => row.ativo === '2'
    }
  ];

  readonly modalPrimaryAction: PoModalAction = { action: () => this.salvar(), label: 'Salvar' };
  readonly modalSecondaryAction: PoModalAction = { action: () => this.formModal.close(), label: 'Cancelar' };

  protected override aoIniciar(): void {
    this.api.filiais().subscribe({
      next: (o: Opcao[]) => this.filiaisOptions = o,
      error: () => this.notif.warning('Não foi possível carregar a lista de filiais.')
    });

    // A TES tem de existir na filial da regra. Só limpa quando havia valor:
    // escrever vazio num combo com serviço ativo esvazia a lista aberta.
    this.formGroup.get('filial')?.valueChanges.subscribe(() => {
      const tes = this.formGroup.get('tes');
      if (tes?.value) {
        tes.setValue('', { emitEvent: false });
      }
    });
  }

  protected override decorar(regra: RegraNfse): RegraNfse {
    return { ...regra, vigencia: this.vigenciaDe(regra.dtIni, regra.dtFim) };
  }

  protected corpo(regra: RegraNfse): Record<string, string> {
    const body: Record<string, string> = {
      filial: regra.filial ?? '',
      tipoProcesso: regra.tipoProcesso,
      geraFinanceiro: regra.geraFinanceiro,
      issRetido: regra.issRetido,
      outrasRet: regra.outrasRet,
      cfop: regra.cfop ?? '',
      tes: regra.tes,
      descricao: regra.descricao ?? '',
      ordem: regra.ordem,
      dtIni: regra.dtIni ?? '',
      dtFim: regra.dtFim ?? '',
      ativo: regra.ativo
    };
    if (regra.codigo) { body['codigo'] = regra.codigo; }
    return body;
  }

  abrirNova(): void {
    this.editing = null;
    this.duplicando = false;
    this.formGroup.reset(this.valoresPadrao());
    this.formModal.open();
  }

  editar(regra: RegraNfse): void {
    this.editing = regra;
    this.duplicando = false;
    this.formGroup.reset({ ...this.valoresPadrao(), ...regra });
    this.formModal.open();
  }

  duplicar(regra: RegraNfse): void {
    this.editing = null;
    this.duplicando = true;
    this.formGroup.reset({ ...this.valoresPadrao(), ...regra, codigo: '', ordem: '' });
    this.formModal.open();
  }

  salvar(): void {
    const regra = this.formGroup.getRawValue() as RegraNfse;
    regra.ordem = this.normalizarOrdem(regra.ordem);

    const erro = this.validar(regra);
    if (erro) {
      this.notif.warning(erro);
      return;
    }

    this.gravar(regra, !!this.editing?.codigo, () => this.formModal.close());
  }

  private validar(f: RegraNfse): string {
    if (!f.tipoProcesso) { return 'Informe o tipo de processo.'; }
    if (!f.geraFinanceiro) { return 'Informe a condição de gera financeiro.'; }
    if (!f.issRetido) { return 'Informe a condição de ISS retido.'; }
    if (!f.outrasRet) { return 'Informe a condição das demais retenções.'; }
    if (!f.tes) { return 'Informe o tipo de operação (TES).'; }
    const ordem = String(f.ordem ?? '').trim();
    if (!ordem || !/^\d{1,3}$/.test(ordem) || Number(ordem) < 1) {
      return 'Informe a ordem de avaliação (1 a 999).';
    }
    if (f.dtIni && f.dtFim && f.dtFim < f.dtIni) {
      return 'O fim da vigência não pode ser anterior ao início.';
    }
    return '';
  }

  private valoresPadrao(): Partial<RegraNfse> {
    return {
      codigo: '', filial: '', tipoProcesso: '', geraFinanceiro: '', issRetido: '',
      outrasRet: '3', cfop: '', tes: '', descricao: '', ordem: '',
      dtIni: this.hojeIso(), dtFim: '', ativo: '1'
    };
  }
}
